import { jsPDF } from 'jspdf';
import 'jspdf-autotable/dist/jspdf.plugin.autotable.min.js';
import html2canvas from 'html2canvas';

export const generateGridScanPDF = async (scanData, processItems, mapImage) => {
  const doc = new jsPDF();
  
  // Ensure autoTable is available
  if (!doc.autoTable) {
    console.error('jsPDF autoTable plugin not loaded');
    return doc;
  }
  
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 25;
  const contentWidth = pageWidth - (margin * 2);
  
  // Color scheme matching reference PDF exactly
  const colors = {
    primary: [99, 102, 241],      // Purple/Indigo 
    secondary: [34, 197, 94],     // Green
    warning: [251, 146, 60],      // Orange
    danger: [239, 68, 68],        // Red
    info: [59, 130, 246],         // Blue
    gray: [156, 163, 175],        // Medium gray
    lightGray: [249, 250, 251],   // Very light gray
    darkGray: [55, 65, 81],       // Dark gray text
    borderGray: [229, 231, 235],  // Light border
    white: [255, 255, 255],
    purple: [139, 92, 246]        // Purple for header
  };
  
  let yPosition = 25;
  
  // HEADER SECTION - Clean and modern design
  // Draw target/bullseye logo
  doc.setDrawColor(...colors.danger);
  doc.setFillColor(...colors.white);
  doc.setLineWidth(2);
  doc.circle(margin, yPosition, 4);
  doc.setFillColor(...colors.danger);
  doc.circle(margin, yPosition, 2, 'F');
  
  // Main title
  doc.setFontSize(26);
  doc.setTextColor(...colors.purple);
  doc.setFont(undefined, 'bold');
  doc.text('GridRank', margin + 12, yPosition + 2);
  
  yPosition += 15;
  doc.setFontSize(18);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(...colors.darkGray);
  doc.text('Grid Scan Performance Report', margin, yPosition);
  
  yPosition += 10;
  doc.setFontSize(12);
  doc.setTextColor(...colors.gray);
  doc.text('Comprehensive Analysis of Your Geographic Search Performance', margin, yPosition);
  
  // Business info section with subtle purple underline
  yPosition += 15;
  doc.setDrawColor(...colors.purple);
  doc.setLineWidth(1);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  
  yPosition += 10;
  const businessName = scanData.businessName || scanData.business_name || 'Business Name';
  const reportDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(...colors.darkGray);
  doc.text('Business: ', margin, yPosition);
  doc.setFont(undefined, 'normal');
  doc.text(businessName, margin + 22, yPosition);
  
  doc.setFont(undefined, 'bold');
  doc.text('Generated: ', pageWidth - margin - 65, yPosition);
  doc.setFont(undefined, 'normal');
  doc.text(reportDate, pageWidth - margin - 40, yPosition);
  
  // Calculate statistics from processItems
  const totalGridPoints = processItems.length;
  const uniqueKeywords = [...new Set(processItems.map(item => item.keyword))];
  const totalKeywords = uniqueKeywords.length;
  
  const rankedItems = processItems.filter(item => item.rank && item.rank > 0);
  const avgRank = rankedItems.length > 0 
    ? rankedItems.reduce((acc, item) => acc + item.rank, 0) / rankedItems.length 
    : 0;
  
  const top3Count = processItems.filter(item => item.rank > 0 && item.rank <= 3).length;
  const top10Count = processItems.filter(item => item.rank > 0 && item.rank <= 10).length;
  const top20Count = processItems.filter(item => item.rank > 0 && item.rank <= 20).length;
  const notRankedCount = processItems.filter(item => !item.rank || item.rank === 0).length;
  
  const top3Percentage = totalGridPoints > 0 ? (top3Count / totalGridPoints) * 100 : 0;
  const coveragePercentage = totalGridPoints > 0 
    ? ((totalGridPoints - notRankedCount) / totalGridPoints) * 100 
    : 0;
  
  // EXECUTIVE SUMMARY with thin green left border
  yPosition += 20;
  
  // Thin green accent bar (like reference)
  doc.setFillColor(...colors.secondary);
  doc.rect(margin, yPosition - 5, .5, 35, 'F');  // Thinner left border
  
  // Bar chart icon
  doc.setFillColor(...colors.gray);
  doc.rect(margin + 8, yPosition, 2, 4, 'F');
  doc.rect(margin + 11, yPosition - 2, 2, 6, 'F');
  doc.rect(margin + 14, yPosition - 1, 2, 5, 'F');
  
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(...colors.darkGray);
  doc.text('Executive Summary', margin + 20, yPosition + 2);
  
  yPosition += 12;
  doc.setFontSize(10.5);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(...colors.darkGray);
  
  const gridSize = scanData.gridSize || '5×5';
  const totalLocations = parseInt(gridSize.split('×')[0]) * parseInt(gridSize.split('×')[1]);
  
  doc.text(`Overall Performance: Your grid scan shows ${top3Percentage.toFixed(0)}% of keyword-location combinations ranking in top 3`, margin + 8, yPosition);
  yPosition += 5;
  doc.text(`positions across ${totalLocations} grid locations and ${totalKeywords} keyword${totalKeywords > 1 ? 's' : ''}. Average ranking position is ${avgRank.toFixed(1)}.`, margin + 8, yPosition);
  yPosition += 8;
  doc.text(`Key Highlights: Grid coverage reached ${coveragePercentage.toFixed(0)}% visibility across all tracked keyword-location combinations.`, margin + 8, yPosition);
  
  // KEY METRICS CARDS - 2x2 layout matching reference
  yPosition += 25;
  const cardWidth = (contentWidth - 15) / 2;  // 2 cards per row
  const cardHeight = 60;
  const cardSpacing = 15;
  const verticalSpacing = 15;
  
  const metricsCards = [
    {
      value: totalKeywords.toString(),
      label: 'KEYWORDS TRACKED',
      sublabelColor: colors.secondary
    },
    {
      value: avgRank.toFixed(1),
      label: 'AVERAGE POSITION',
      sublabelColor: colors.secondary
    },
    {
      value: `${top3Percentage.toFixed(0)}%`,
      label: 'TOP 3 POSITIONS',
      sublabelColor: colors.secondary
    },
    {
      value: `${coveragePercentage.toFixed(0)}%`,
      label: 'GRID COVERAGE',
      sublabelColor: colors.danger
    }
  ];
  
  metricsCards.forEach((card, index) => {
    const row = Math.floor(index / 2);
    const col = index % 2;
    const xPos = margin + (col * (cardWidth + cardSpacing));
    const yPos = yPosition + (row * (cardHeight + verticalSpacing));
    
    // White card with very thin border (matching reference)
    doc.setFillColor(...colors.white);
    doc.setDrawColor(...colors.borderGray);
    doc.setLineWidth(0.5);  // Very thin border like reference
    doc.roundedRect(xPos, yPos, cardWidth, cardHeight, 3, 3);
    
    // Large value
    doc.setFontSize(32);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...colors.purple);
    doc.text(card.value, xPos + cardWidth/2, yPos + 28, { align: 'center' });
    
    // Label
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...colors.darkGray);
    doc.text(card.label, xPos + cardWidth/2, yPos + 40, { align: 'center' });
  });
  
  // POSITION PERFORMANCE BREAKDOWN - Matching reference cards exactly
  yPosition += 135;  // Adjusted for 2x2 layout
  
  // Check if we need a new page for Position Performance section
  if (yPosition + 150 > pageHeight - margin) {
    doc.addPage();
    yPosition = margin;
  }
  
  // Bar chart icon
  doc.setFillColor(...colors.gray);
  doc.rect(margin, yPosition, 1.5, 5, 'F');
  doc.rect(margin + 2.5, yPosition - 2, 1.5, 7, 'F');
  doc.rect(margin + 5, yPosition - 1, 1.5, 6, 'F');
  
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(...colors.darkGray);
  doc.text('Position Performance Breakdown', margin + 10, yPosition + 2);
  
  yPosition += 20;  // Space after heading
  
  // Position cards with colored borders - 2 per row layout
  const positionCards = [
    {
      value: top3Count,
      label: 'POSITIONS 1-3',
      borderColor: colors.secondary
    },
    {
      value: top10Count - top3Count,
      label: 'POSITIONS 4-7',
      borderColor: colors.purple
    },
    {
      value: top20Count - top10Count,
      label: 'POSITIONS 8-15',
      borderColor: colors.warning
    },
    {
      value: rankedItems.length - top20Count,
      label: 'POSITION 16+',
      borderColor: colors.danger
    },
    {
      value: notRankedCount,
      label: 'NOT RANKED',
      borderColor: [200, 200, 200]
    }
  ];
  
  // 2 cards per row, last one full width if alone
  const defaultCardWidth = (contentWidth - 15) / 2;
  const fullCardWidth = contentWidth;
  const posCardHeight = 55;
  const posCardSpacing = 15;
  const posVerticalSpacing = 15;
  
  positionCards.forEach((card, index) => {
    let xPos, yPos, cardWidth;
    
    const row = Math.floor(index / 2);
    const col = index % 2;
    const isLastCard = index === positionCards.length - 1;
    const isAloneInRow = isLastCard && (positionCards.length % 2 === 1);
    
    if (isAloneInRow) {
      // Last card alone - full width
      cardWidth = fullCardWidth;
      xPos = margin;
      yPos = yPosition + (row * (posCardHeight + posVerticalSpacing));
    } else {
      // Regular 2 per row layout
      cardWidth = defaultCardWidth;
      xPos = margin + (col * (cardWidth + posCardSpacing));
      yPos = yPosition + (row * (posCardHeight + posVerticalSpacing));
    }
    
    // White background
    doc.setFillColor(...colors.white);
    doc.rect(xPos, yPos, cardWidth, posCardHeight, 'F');
    
    // Colored border - very thin like reference
    doc.setDrawColor(...card.borderColor);
    doc.setLineWidth(0.5);  // Very thin border
    doc.roundedRect(xPos, yPos, cardWidth, posCardHeight, 3, 3);
    
    // Value
    doc.setFontSize(28);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...card.borderColor);
    doc.text(card.value.toString(), xPos + cardWidth/2, yPos + 28, { align: 'center' });
    
    // Label
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...colors.darkGray);
    doc.text(card.label, xPos + cardWidth/2, yPos + 42, { align: 'center' });
  });
  
  // Update yPosition to account for the actual number of rows
  const totalRows = Math.ceil(positionCards.length / 2);
  yPosition += (posCardHeight * totalRows) + (posVerticalSpacing * (totalRows - 1)) + 20;
  
  // GEOGRAPHIC GRID PERFORMANCE MAP - Start on new page
  doc.addPage();
  yPosition = margin;
  
  // Add section title on new page
  doc.setFontSize(16);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(...colors.darkGray);
  doc.text('Average Geographic Performance Overview', pageWidth/2, yPosition, { align: 'center' });
  
  yPosition += 15;
  
  // Use the provided map image if available (PNG export from the section)
  if (mapImage) {
    try {
      // Calculate dimensions to fit the page properly
      const maxWidth = pageWidth - (margin * 2);
      const maxHeight = pageHeight - yPosition - margin - 20;
      
      // Maintain aspect ratio
      const imgWidth = maxWidth;
      const imgHeight = Math.min(maxHeight, 100); // Adjust height as needed
      
      doc.addImage(mapImage, 'PNG', margin, yPosition, imgWidth, imgHeight);
      yPosition += imgHeight + 15;
      
    } catch (error) {
      console.log('Could not add map image, adding text description');
      // Fallback: Add text description
      doc.setFontSize(10);
      doc.setTextColor(...colors.gray);
      doc.text('Map visualization not available in this export', pageWidth/2, yPosition, { align: 'center' });
      yPosition += 20;
    }
  } else {
    // No map image provided
    doc.setFontSize(10);
    doc.setTextColor(...colors.gray);
    doc.text('Map visualization not available in this export', pageWidth/2, yPosition, { align: 'center' });
    yPosition += 20;
  }
  
  // Add grid information
  doc.setFontSize(9);
  doc.setTextColor(...colors.gray);
  doc.setFont(undefined, 'normal');
  doc.text(`Grid Size: ${gridSize}`, margin, yPosition);
  doc.text(`Total Grid Points: ${processItems.length}`, pageWidth/2, yPosition, { align: 'center' });
  doc.text(`Keywords: ${uniqueKeywords.join(', ')}`, pageWidth - margin, yPosition, { align: 'right' });
  
  // ADD DETAILED PERFORMANCE TABLE - New page
  doc.addPage();
  yPosition = margin;
  
  // Table header
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(...colors.darkGray);
  doc.text('Detailed Performance Data', pageWidth/2, yPosition, { align: 'center' });
  
  yPosition += 15;
  
  // Prepare table data
  const tableHeaders = ['Grid Point', 'Keyword', 'Rank', 'Status', 'Latitude', 'Longitude'];
  const tableRows = processItems.map((item, index) => {
    const rank = item.rank === 0 ? '20+' : (item.rank || 'Not Ranked');
    return [
      `Point ${item.grid_index || index + 1}`,
      item.keyword || '',
      rank.toString(),
      item.status || 'completed',
      item.latitude ? parseFloat(item.latitude).toFixed(6) : '',
      item.longitude ? parseFloat(item.longitude).toFixed(6) : ''
    ];
  });
  
  // Add table using autoTable
  doc.autoTable({
    startY: yPosition,
    head: [tableHeaders],
    body: tableRows,
    theme: 'grid',
    headStyles: {
      fillColor: colors.purple,
      textColor: colors.white,
      fontSize: 10,
      fontStyle: 'bold'
    },
    bodyStyles: {
      fontSize: 9,
      textColor: colors.darkGray
    },
    alternateRowStyles: {
      fillColor: colors.lightGray
    },
    margin: { left: margin, right: margin },
    columnStyles: {
      0: { cellWidth: 25 },
      1: { cellWidth: 40 },
      2: { cellWidth: 20, halign: 'center' },
      3: { cellWidth: 25, halign: 'center' },
      4: { cellWidth: 30 },
      5: { cellWidth: 30 }
    }
  });
  
  // Add footer on last page
  const finalY = doc.autoTable.previous.finalY || yPosition;
  if (finalY + 40 < pageHeight - margin) {
    yPosition = pageHeight - margin - 20;
  } else {
    doc.addPage();
    yPosition = pageHeight - margin - 20;
  }
  
  // Footer
  doc.setDrawColor(...colors.borderGray);
  doc.setLineWidth(0.5);
  doc.line(margin, yPosition - 10, pageWidth - margin, yPosition - 10);
  
  doc.setFontSize(8);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(...colors.gray);
  doc.text('© GridRank - Grid Scan Performance Report', pageWidth/2, yPosition, { align: 'center' });
  doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageWidth - margin, yPosition, { align: 'right' });
  doc.text(new Date().toLocaleString(), margin, yPosition);
  
  return doc;
};